import type { Block, Blocks } from '@w/content-blocks'
import { practiceClozes } from '@w/content-blocks/src/rule-practice'

type QuestionGroup = Extract<Block, { type: 'questionGroup' }>

/**
 * A question group (flashcards-training.requirements.md 2026-11-02) is several cards: each recall question under its own
 * qid, and its `.practice` questions under one shared qid (the parser mints it, as for a rule). A card shows the group's
 * context (every child that is not a question: heading, image, text) and only its own question; the practice card
 * shows one practice question, the one at the learner's `reviewCount` mod their count, in document order.
 */
export function groupPracticeQuestions(group: QuestionGroup): Block[] {
  return group.children.filter(block => isPracticeItem(block))
}

/** A practice item is a child that is a `.practice` question or holds `.practice` clozes (a paragraph, a list item). */
function isPracticeItem(block: Block): boolean {
  return practiceClozes([block]).length > 0
}

/** The qid the group's practice questions and clozes share (the parser shares one), if it has any. */
export function groupPracticeQid(group: QuestionGroup): string | undefined {
  return practiceClozes(group.children)[0]?.qid
}

/** The practice question shown at this review count. */
export function shownPracticeQuestion(group: QuestionGroup, reviewCount: number): Block | undefined {
  const practice = groupPracticeQuestions(group)
  return practice.length > 0 ? practice[((reviewCount % practice.length) + practice.length) % practice.length] : undefined
}

/** The group as card `card` shows it; without `card` (the page) every recall question stays and one practice question shows. */
export function questionGroupCardBlocks(group: QuestionGroup, reviewCount: number, card?: string): Blocks {
  const shown = shownPracticeQuestion(group, reviewCount)
  const practiceQid = groupPracticeQid(group)
  return group.children.filter(block => {
    if (isPracticeItem(block)) return block.id === shown?.id && (card === undefined || card === practiceQid)
    if (block.type !== 'question') return true
    return card === undefined || block.qid === card
  })
}
